import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";

export interface TemplatePrompt {
  id: string;
  template_id: string;
  prompt: string;
  system_prompt?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface TemplatePromptFormData {
  template_id: string;
  prompt: string;
  system_prompt?: string;
}

export interface TemplateWithPrompt {
  id: string;
  name: string;
  description: string;
  file_path: string;
  thumbnail_url: string;
  created_at: string;
  updated_at: string;
  prompt?: TemplatePrompt;
}

/**
 * Get all template prompts (admin only)
 */
export const getAllTemplatePrompts = async (): Promise<{
  success: boolean;
  data?: TemplatePrompt[];
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('template_prompts')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error: any) {
    console.error('Error fetching template prompts:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all templates with their associated prompts (admin only)
 */
export const getTemplatesWithPrompts = async (): Promise<{
  success: boolean;
  data?: TemplateWithPrompt[];
  error?: string;
}> => {
  try {
    console.log('🔍 Fetching templates with prompts...');

    // First, get all templates from the correct table
    const { data: templatesData, error: templatesError } = await supabase
      .from('templates')
      .select('*')
      .order('created_at', { ascending: false });

    if (templatesError) {
      console.error('❌ Error fetching templates:', templatesError);
      throw templatesError;
    }

    console.log(`📋 Found ${templatesData?.length || 0} templates`);

    // Then get all template prompts
    const { data: promptsData, error: promptsError } = await supabase
      .from('template_prompts')
      .select('*');

    if (promptsError) {
      console.error('❌ Error fetching template prompts:', promptsError);
      // Don't throw error for prompts, just log it
      console.log('⚠️ Continuing without prompts data');
    }

    console.log(`💬 Found ${promptsData?.length || 0} template prompts`);

    // Transform the data to combine templates with their prompts
    const templatesWithPrompts: TemplateWithPrompt[] = (templatesData || []).map(template => {
      const templatePrompt = promptsData?.find(prompt => prompt.template_id === template.id);

      return {
        id: template.id,
        name: template.name || template.title || 'Untitled Template',
        description: template.description,
        file_path: template.file_path,
        thumbnail_url: template.thumbnail_url,
        created_at: template.created_at,
        updated_at: template.updated_at,
        prompt: templatePrompt ? {
          id: templatePrompt.id,
          template_id: templatePrompt.template_id,
          prompt: templatePrompt.prompt,
          system_prompt: templatePrompt.system_prompt,
          created_by: templatePrompt.created_by,
          created_at: templatePrompt.created_at,
          updated_at: templatePrompt.updated_at
        } : undefined
      };
    });

    console.log(`✅ Successfully processed ${templatesWithPrompts.length} templates with prompts`);
    console.log('📊 Templates with prompts:', templatesWithPrompts.map(t => ({
      name: t.name,
      hasPrompt: !!t.prompt,
      promptLength: t.prompt?.prompt?.length || 0
    })));

    return { success: true, data: templatesWithPrompts };
  } catch (error: any) {
    console.error('❌ Error fetching templates with prompts:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get prompt by template ID
 */
export const getTemplatePrompt = async (templateId: string): Promise<{
  success: boolean;
  data?: TemplatePrompt;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('template_prompts')
      .select('*')
      .eq('template_id', templateId)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      throw error;
    }

    return { success: true, data: data || undefined };
  } catch (error: any) {
    console.error('Error fetching template prompt:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create or update template prompt (upsert)
 */
export const upsertTemplatePrompt = async (
  templateId: string,
  prompt: string,
  systemPrompt?: string
): Promise<{
  success: boolean;
  data?: TemplatePrompt;
  error?: string;
}> => {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const updateData: any = {
      template_id: templateId,
      prompt: prompt,
      created_by: user.id,
      updated_at: new Date().toISOString()
    };

    // Include system_prompt if provided
    if (systemPrompt !== undefined) {
      updateData.system_prompt = systemPrompt || null;
    }

    const { data, error } = await supabase
      .from('template_prompts')
      .upsert(updateData, {
        onConflict: 'template_id'
      })
      .select()
      .single();

    if (error) throw error;

    console.log(`✅ Template prompt ${data ? 'updated' : 'created'} for template: ${templateId}`);
    return { success: true, data };
  } catch (error: any) {
    console.error('Error upserting template prompt:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete template prompt
 */
export const deleteTemplatePrompt = async (templateId: string): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const { error } = await supabase
      .from('template_prompts')
      .delete()
      .eq('template_id', templateId);

    if (error) throw error;

    console.log(`✅ Template prompt deleted for template: ${templateId}`);
    return { success: true };
  } catch (error: any) {
    console.error('Error deleting template prompt:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Validate template exists before creating prompt
 */
export const validateTemplateExists = async (templateId: string): Promise<{
  success: boolean;
  exists: boolean;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('templates')
      .select('id')
      .eq('id', templateId)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return { success: true, exists: !!data };
  } catch (error: any) {
    console.error('Error validating template:', error);
    return { success: false, exists: false, error: error.message };
  }
};

/**
 * Get template structure/content for preview (helper function)
 */
export const getTemplateStructure = async (templateId: string): Promise<{
  success: boolean;
  data?: any;
  error?: string;
}> => {
  try {
    const { data: template, error } = await supabase
      .from('templates')
      .select('file_path')
      .eq('id', templateId)
      .single();

    if (error) throw error;

    // TODO: Load template content from Supabase storage
    // This would require implementing template file loading
    // For now, return basic structure info

    return {
      success: true,
      data: {
        file_path: template.file_path,
        message: 'Template structure loading not implemented yet'
      }
    };
  } catch (error: any) {
    console.error('Error getting template structure:', error);
    return { success: false, error: error.message };
  }
};
